import classNames from 'classnames';
import { inject, PropTypes as MobxPropTypes, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import globalTranslations from '../../../../i18n/globalTranslations';
import FormModel from '../../../../models/Form';
import PaymentMethod from '../../../../models/order/checkout/payment/PaymentMethod';
import PaymentMethodGroup from '../../../../models/order/checkout/payment/PaymentMethodGroup';
import ShippingOption from '../../../../models/ShippingOption';
import { modelOf } from '../../../../prop-types';
import AccountStore from '../../../../store/AccountStore';
import UIStore from '../../../../store/UIStore';
import { createOption } from '../../../form/Field';
import Price from '../../../product/Price';
import MethodImage from '../../../common/MethodImage';
import CheckoutMethodSelector from '../CheckoutMethodSelector';

const CheckoutServiceMethodList = ({
  accountStore,
  uiStore,
  onMethodUpdate,
  fieldName,
  field,
  form,
  options,
  content,
  optionContent,
  className,
  useMobileStackedLayout,
  methodImageVariant,
}) => {
  const selectorField = form.getField(field);
  const withTax = accountStore.showPricesWithTax;

  const getPriceContent = (option) => {
    if (option.has_free_shipping) {
      return <FormattedMessage {...globalTranslations.freeDeliverySentence} />;
    }
    return <Price price={option.price.getPrice(withTax)} />;
  };

  const renderMethodPrice = (
    option,
    className = 'CheckoutServiceMethodList__selector-price'
  ) => {
    return <span className={className}>{getPriceContent(option)}</span>;
  };

  const createInputOptions = () => {
    return options.map((option) => {
      const value = option.type || option.id;
      const type = option.type || option.class || option.parent_group_type;

      return createOption({
        disabled: option.is_denied,
        value,
        content: optionContent && optionContent(option),
        label: (
          <div
            className={classNames(
              'CheckoutServiceMethodList__selector',
              `CheckoutServiceMethodList__selector-${type.toLowerCase()}`,
              {
                'CheckoutServiceMethodList__selector--active':
                  value === selectorField.value,
                'CheckoutServiceMethodList__selector--disabled':
                  option.is_denied,
                'CheckoutServiceMethodList__selector--stacked-mobile':
                  useMobileStackedLayout,
              }
            )}
          >
            <div className="CheckoutServiceMethodList__selector-details">
              {(option.logo || option.image) && (
                <MethodImage method={option} variant={methodImageVariant} />
              )}
              {(!useMobileStackedLayout || !uiStore.isMobile) && (
                <span className="CheckoutServiceMethodList__selector-title">
                  {option.name}
                </span>
              )}
              {useMobileStackedLayout &&
                uiStore.isMobile &&
                option.hasPrice &&
                renderMethodPrice(
                  option,
                  'CheckoutServiceMethodList__selector-price-mobile'
                )}
            </div>
            {(!useMobileStackedLayout || !uiStore.isMobile) &&
              option.hasPrice &&
              renderMethodPrice(option)}
            {useMobileStackedLayout && uiStore.isMobile && (
              <span className="CheckoutServiceMethodList__selector-title-mobile">
                {option.name}
              </span>
            )}
            {option.is_denied && (
              <div className="CheckoutServiceMethodList__selector-denied-message">
                <p>{option.denied_message}</p>
              </div>
            )}
          </div>
        ),
      });
    });
  };

  return (
    <div className={classNames('CheckoutServiceMethodList', className)}>
      <CheckoutMethodSelector
        form={form}
        onChange={onMethodUpdate}
        options={createInputOptions()}
        fieldName={fieldName}
        field={field}
        content={content}
      />
    </div>
  );
};

CheckoutServiceMethodList.propTypes = {
  accountStore: modelOf(AccountStore),
  uiStore: modelOf(UIStore).isRequired,
  form: modelOf(FormModel),
  options: MobxPropTypes.arrayOrObservableArrayOf(
    PropTypes.object,
    PropTypes.oneOfType([modelOf(PaymentMethodGroup)]),
    PropTypes.oneOfType([modelOf(PaymentMethod)]),
    PropTypes.oneOfType([modelOf(ShippingOption)])
  ).isRequired,
  fieldName: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  onMethodUpdate: PropTypes.func.isRequired,
  className: PropTypes.string,
  content: PropTypes.func,
  optionContent: PropTypes.func,
  useMobileStackedLayout: PropTypes.bool,
  methodImageVariant: PropTypes.oneOf([
    'default',
    'large',
    'checkout-method-list',
    'checkout-method-card',
  ]),
};

export default inject(
  'accountStore',
  'uiStore'
)(observer(CheckoutServiceMethodList));
